import { useState, useRef, useEffect } from 'react';
import { clearClientSession } from '../../services/api';
import Profile from '../../assets/user1.png';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { Activity, CheckCircle, AlertCircle, Clock, X, LogOut, Settings, Zap, Bell, Menu } from 'lucide-react';
import { FaInfoCircle } from "react-icons/fa";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";


const Navbar = ({ notifications, markNotificationAsRead, dismissNotification, markAllAsRead, getUnreadCount, onOpenMobileSidebar }) => {
  const { session } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const currentApp = sessionStorage.getItem('selectedApp');
  const navigate = useNavigate();
  const location = useLocation();
  const isApplicationPage = location.pathname === '/application-dashboard';
  const isGuardrailPage = location.pathname === '/guardrail-analysis';
  const isSettingsPage = location.pathname === '/settings';

  const handleLogout = async () => {
    if (!await clearClientSession()) return;
    navigate("/");
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    setShowDropdown(false); // Close profile dropdown if open
    if (!showNotifications) {
      markAllAsRead(); // Mark all as read when opening the notification sidebar
    }
  };

  const getNotificationIconAndColor = (type, source) => {
    if (source === "System Prompt Hardening") {
      if (type === "success") return { Icon: Zap, color: "text-green-400", bgColor: "bg-green-400/10" };
      if (type === "error") return { Icon: Zap, color: "text-red-400", bgColor: "bg-red-400/10" };
      return { Icon: Zap, color: "text-blue-400", bgColor: "bg-blue-400/10" }; // Info for System Prompt Hardening
    }

    // Default icons and colors for other notification types
    if (type === "scan") return { Icon: Activity, color: "text-blue-400", bgColor: "bg-blue-400/10" };
    if (type === "completed") return { Icon: CheckCircle, color: "text-green-400", bgColor: "bg-green-400/10" };
    if (type === "alert") return { Icon: AlertCircle, color: "text-red-400", bgColor: "bg-red-400/10" };
    if (type === "pending") return { Icon: Clock, color: "text-yellow-400", bgColor: "bg-yellow-400/10" };
    return { Icon: Activity, color: "text-gray-400", bgColor: "bg-gray-400/10" }; // Fallback
  };

  const getBellIconColor = () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length > 0) {
      const latestNotification = unreadNotifications[0]; // Assuming newest are at the beginning
      if (latestNotification.source === "System Prompt Hardening") {
        if (latestNotification.type === "success") return "text-green-400";
        if (latestNotification.type === "error") return "text-red-400";
        return "text-blue-400"; // Info
      }
      // Default color for other types of notifications if they are unread
      if (latestNotification.type === "completed") return "text-green-400";
      if (latestNotification.type === "alert") return "text-red-400";
      if (latestNotification.type === "scan") return "text-blue-400";
      if (latestNotification.type === "pending") return "text-yellow-400";
    }
    return "text-gray-400"; // Default color if no unread notifications
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const startTour = () => {
    const driverObj = driver({
      showProgress: true,
      overlayOpacity: 0.4,
      allowClose: true,
      smoothScroll: true,
      steps: [
        { element: '#sidebar', popover: { title: 'Walkthrough', description: 'Let\'s walk you through the functions of LLMINATE.', side: "right", align: 'start' }},
        { element: '#dashboard', popover: {title: 'Dashboard', description: 'The one place where you can see all the statistics about your application', side: "right", align: 'start'}},
        { element: '#context', popover: {title: 'Application Context', description: 'Here you can provide context about your model and review your framework', side: "right", align: 'start'}},
        { element: '#redteam', popover: {title: 'Red-team', description: 'Create new scans, save your configurations and view reports', side: "right", align: 'start'}},
        { element: '#archanalysis', popover: {title: 'Architecture-analysis', description: 'Analyse your model and code-base architecture', side: "right", align: 'start'}},
        { element: '#agentic-radar', popover: {title: 'Agentic Radar', description: 'Scan your Agentic workflows', side: "right", align: 'start'}},
        { element: '#mcp-scan', popover: {title: 'MCP Scanner', description: 'Scan your MCP configurations', side: "right", align: 'start'}},
        // { element: '#governance', popover: {title: 'Governance', description: 'Analyse and Talk with your Governance Policies', side: "right", align: 'start'}},
        { element: '#prompt-hardening', popover: {title: 'Prompt-hardening', description: 'Harden your system prompt', side: "right", align: 'start'}},
        { element: '#guardrails', popover: {title: 'Gaurdrail Analysis', description: 'Analyse how efficient your Gaurdrails are', side: "right", align: 'start'}},
        { element: '#settings', popover: {title: 'User Settings', description: 'Edit the users in your application', side: "right", align: 'start'}},
        { element: '#walkthrough', popover: { title: 'Revisit the walkthrough', description: 'You can take the walkthrough again from the dashboard, whenever you like' } }
      ]
    });

    driverObj.drive();
  };

  return (
    <>
      <div className="min-h-[10vh] border-b border-[#232325] border-2 bg-[#0D0D0E] flex flex-row items-center justify-between relative z-40">
        <div className="flex items-center gap-4">
          {/* Hamburger button — mobile only (hidden at md+) */}
          <button
            className="md:hidden flex items-center justify-center ml-4 p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all duration-200"
            onClick={onOpenMobileSidebar}
            aria-label="Open navigation menu"
          >
            <Menu size={20} className="text-white" />
          </button>

          <div className="ml-4 md:ml-12 flex items-center gap-4">
            {!isApplicationPage && !isGuardrailPage && !isSettingsPage && (
              <div className='flex items-center gap-3'>
                <span className='text-white font-medium text-lg hidden sm:block'>Application:</span>
                <span className='text-blue-400 font-semibold text-lg bg-blue-500/10 px-4 py-1.5 rounded-lg border border-blue-500/20'>
                  {currentApp}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mr-6">
          <div className="relative flex flex-row gap-4">
            {/* {location.pathname === "/dashboard" && (
              <button
                id="walkthrough"
                onClick={startTour}
                className="relative p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all duration-200 group"
              >
                <FaInfoCircle size={20} className="text-white" />
              </button>
            )} */}
            <button
                id="walkthrough"
                onClick={startTour}
                className="relative p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all duration-200 group"
              >
                <FaInfoCircle size={20} className="text-white" />
            </button>
            <button
              onClick={handleNotificationClick}
              className="relative p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all duration-200 group"
            >
              <Bell size={20} className={`opacity-70 group-hover:opacity-100 transition-opacity ${getBellIconColor()}`} />
              {getUnreadCount() > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full border-2 border-[#0D0D0E]">
                  {getUnreadCount() > 9 ? '9+' : getUnreadCount()}
                </span>
              )}
            </button>
          </div>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setShowDropdown((prev) => !prev)}
              className="flex items-center gap-3 p-2 rounded-xl bg-[#1a1a1c] hover:bg-[#232325] border border-[#232325] transition-all duration-200 group"
            >
              <img
                src={Profile}
                alt="profile"
                className="h-5 w-5 opacity-70 group-hover:opacity-100 transition-opacity"
              />
              <span className="hidden md:block text-gray-400 group-hover:text-white transition-colors">
                {session?.user?.email}
              </span>
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 py-2 bg-[#1a1a1c] border border-[#232325] rounded-xl shadow-lg backdrop-blur-sm animate-in slide-in-from-top-2 duration-200 z-50">
                <div className="px-4 py-2 border-b border-[#232325]">
                  <p className="text-sm text-gray-400">Signed in as</p>
                  <p className="text-sm font-medium text-white truncate">{session?.user?.email}</p>
                </div>

                <button
                  onClick={() => {navigate('/settings')}}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-400 hover:text-white hover:bg-[#232325] transition-colors"
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-[#232325] transition-colors"
                >
                  <LogOut size={16} />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notifications Sidebar */}
      {showNotifications && (
        <div
          ref={notificationRef}
          className="fixed right-0 top-[10vh] h-[90vh] w-96 bg-[#0D0D0E] border-l border-[#232325] shadow-2xl z-50 overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-[#232325] flex items-center justify-between">
            <h2 className="text-white font-bold text-lg">Notifications</h2>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Mark all as read
                </button>
              )}
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Bell size={48} className="mb-4 opacity-50" />
                <p className="text-center">No notifications</p>
              </div>
            ) : (
              <div className="p-2">
                {notifications.map((notification) => {
                  const iconData = getNotificationIconAndColor(notification.type, notification.source);
                  const IconComponent = iconData.Icon;
                  return (
                    <div
                      key={notification.id}
                      className="mb-2 p-3 bg-[#1a1a1c] border border-[#232325] rounded-lg hover:bg-[#1f1f21] transition-colors group"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${iconData.bgColor} flex-shrink-0`}>
                          <IconComponent size={16} className={iconData.color} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <h3 className="text-white font-medium text-sm leading-tight">
                              {notification.message}
                            </h3>
                            <button
                              onClick={() => dismissNotification(notification.id)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-all ml-2 flex-shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </div>

                          <p className="text-gray-500 text-xs mt-2">
                            {notification.timestamp}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay */}
      {showNotifications && (
        <div
          className="fixed inset-0 backdrop-blur-sm  bg-opacity-20 z-30"
          onClick={() => setShowNotifications(false)}
        />
      )}
    </>
  );
};


export default Navbar;
